import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CommentStatus = "pending" | "error" | "published";

export interface PendingComment {
  uid: string;
  text: string;
  status: CommentStatus;
  replyToUid?: string;
  level?: any;
}

interface CommentPendingState {
  items: PendingComment[];
  retryText?: { uid: string; text: string };
}

const initialState: CommentPendingState = {
  items: [],
  retryText: undefined,
};

const commentPendingSlice = createSlice({
  name: "commentPending",
  initialState,
  reducers: {
    addPendingComment: (
      state,
      action: PayloadAction<{
        level: number;
        replyToUid?: string;
        uid: string;
        text: string;
      }>
    ) => {
      state.items.push({
        uid: action.payload.uid,
        text: action.payload.text,
        status: "pending",
        level: action.payload.level,
        replyToUid: action.payload.replyToUid,
      });
    },
    updatePendingCommentStatus: (
      state,
      action: PayloadAction<{ uid: string; status: CommentStatus }>
    ) => {
      const item = state.items.find((i) => i.uid === action.payload.uid);
      if (item) {
        item.status = action.payload.status;
      }
    },
    removePendingComment: (state, action: PayloadAction<{ uid: string }>) => {
      state.items = state.items.filter((i) => i.uid !== action.payload.uid);
    },
    setRetryText: (state, action: PayloadAction<{ uid: string; text: string } | undefined>) => {
      state.retryText = action.payload;
    },
    updatePendingCommentUid: (state, action) => {
      const { oldUid, newUid } = action.payload;

      const item = state.items.find((i) => i.uid === oldUid);
      if (item) {
        item.uid = newUid;
      }

      state.items.forEach((i) => {
        if (i.replyToUid === oldUid) {
          i.replyToUid = newUid;
        }
      });
    },

    updatePendingCommentAfterPublish: (
      state,
      action: PayloadAction<{ oldUid: string; newUid: string }>
    ) => {
      const item = state.items.find((i) => i.uid === action.payload.oldUid);
      if (!item) return;

      item.uid = action.payload.newUid;
      item.status = "published";

      state.items.forEach((i) => {
        if (i.replyToUid === action.payload.oldUid) {
          i.replyToUid = action.payload.newUid;
        }
      });
    },
  },
});

export const {
  addPendingComment,
  updatePendingCommentStatus,
  updatePendingCommentUid,
  removePendingComment,
  setRetryText,
  updatePendingCommentAfterPublish,
} = commentPendingSlice.actions;

export default commentPendingSlice.reducer;
